import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { cookieConsentService } from '../services/cookieConsent';
import { captureCookieConsentChoice } from '../services/analytics/lazyAnalytics';
import { COOKIE_POLICY_PATH, localizePublicHref } from '../utils/publicRoutes';

const COOKIE_BANNER_COPY = {
  fr: {
    title: 'Cookies',
    description: "Palen utilise des cookies nécessaires au fonctionnement du service.",
    audienceMeasurement: "Mesure d’audience facultative",
    audienceMeasurementDescription: "Lorsqu’elle est activée, nous aide à comprendre les pages consultées, les erreurs et les parcours difficiles afin d’améliorer Palen.",
    communicationTracking: 'Mesure des actions de communication',
    communicationTrackingDescription: "Nous aide à mesurer l'efficacité de nos liens et campagnes de communication, notamment via LinkedIn Insight.",
    configure: 'Configurer',
    save: 'Enregistrer vos choix',
    accept: 'Tout accepter',
  },
  en: {
    title: 'Cookies',
    description: 'Palen uses cookies required for the service to work.',
    audienceMeasurement: 'Optional audience measurement',
    audienceMeasurementDescription: 'When enabled, helps us understand viewed pages, errors and difficult flows so we can improve Palen.',
    communicationTracking: 'Communication action measurement',
    communicationTrackingDescription: 'Helps us measure the effectiveness of our links and communication campaigns, including LinkedIn Insight.',
    configure: 'Configure',
    save: 'Save my choices',
    accept: 'Accept all',
  },
};

const COOKIE_BANNER_SPACE_VARIABLE = '--cookie-banner-space';
const COOKIE_BANNER_FADE_DURATION_MS = 220;

interface CookieOptionCardProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function CookieOptionCard({
  title,
  description,
  checked,
  onChange,
}: CookieOptionCardProps) {
  return (
    <label className="theme-cookie-option group flex cursor-pointer items-start justify-between gap-3 rounded-lg border p-3 transition-colors">
      <span className="min-w-0">
        <span className="theme-cookie-banner-title block text-sm font-bold">{title}</span>
        <span className="theme-cookie-banner-copy mt-1 block text-xs leading-5">{description}</span>
      </span>
      <span className={`relative mt-1 inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors ${checked ? 'border-orange-300 bg-orange-300' : 'border-white/20 bg-slate-800'}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="sr-only"
        />
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </span>
    </label>
  );
}

export function CookieInformationBanner() {
  const { language, t } = useTranslation();
  const bannerRef = useRef<HTMLElement>(null);
  const exitTimerRef = useRef<number | null>(null);
  // A static prerender cannot know the visitor's cookie state. Waiting until
  // after hydration keeps the server and first browser render identical.
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [audienceMeasurement, setAudienceMeasurement] = useState(true);
  const [communicationTracking, setCommunicationTracking] = useState(true);
  const copy = COOKIE_BANNER_COPY[language];

  useEffect(() => {
    const syncVisibility = () => {
      setIsVisible(cookieConsentService.readStatus() === null);
    };

    syncVisibility();
    return cookieConsentService.onChange(syncVisibility);
  }, []);

  useEffect(() => {
    return cookieConsentService.onPreferencesRequested(() => {
      const preferences = cookieConsentService.readPreferences();
      setAudienceMeasurement(preferences.audienceMeasurement);
      setCommunicationTracking(preferences.communicationTracking);
      setIsConfiguring(true);
      setIsVisible(true);
    });
  }, []);

  useEffect(() => {
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (isVisible) {
      setShouldRender(true);
      return;
    }

    if (shouldRender) {
      exitTimerRef.current = window.setTimeout(() => {
        setShouldRender(false);
        exitTimerRef.current = null;
      }, COOKIE_BANNER_FADE_DURATION_MS);
    }

    return () => {
      if (exitTimerRef.current !== null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [isVisible, shouldRender]);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!isVisible || !banner) {
      document.documentElement.style.removeProperty(COOKIE_BANNER_SPACE_VARIABLE);
      return;
    }

    const updateReservedSpace = () => {
      document.documentElement.style.setProperty(
        COOKIE_BANNER_SPACE_VARIABLE,
        `${banner.offsetHeight + 24}px`,
      );
    };

    updateReservedSpace();
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        document.documentElement.style.removeProperty(COOKIE_BANNER_SPACE_VARIABLE);
      };
    }

    const resizeObserver = new ResizeObserver(updateReservedSpace);
    resizeObserver.observe(banner);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.removeProperty(COOKIE_BANNER_SPACE_VARIABLE);
    };
  }, [isConfiguring, isVisible]);

  const acceptAll = () => {
    cookieConsentService.accept();
    void captureCookieConsentChoice({
      audienceMeasurement: true,
      communicationTracking: true,
    });
    setIsVisible(false);
  };

  const saveConfiguration = () => {
    cookieConsentService.configure({
      audienceMeasurement,
      communicationTracking,
    });
    void captureCookieConsentChoice({
      audienceMeasurement,
      communicationTracking,
    });
    setIsVisible(false);
  };

  return (
    shouldRender ? (
      <aside
          ref={bannerRef}
          className={`theme-cookie-banner fixed inset-x-3 bottom-3 z-[80] mx-auto max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-xl sm:max-h-[calc(100dvh-2.5rem)] ${
            isConfiguring ? 'max-w-5xl p-4 sm:bottom-5 sm:p-5' : 'max-w-2xl p-3 sm:bottom-4'
          } ${
            isVisible ? 'animate-app-fade-in' : 'animate-app-fade-out'
          }`}
          aria-label={copy.title}
        >
          <div className={`flex flex-col ${isConfiguring ? 'gap-4 md:flex-row md:items-center md:justify-between' : 'gap-3 sm:flex-row sm:items-center sm:justify-between'}`}>
            <div className="min-w-0">
              <p className={`theme-cookie-banner-copy text-center ${isConfiguring ? 'text-sm leading-6 md:text-left' : 'text-xs leading-5 sm:text-left'}`}>
                {copy.description}{' '}
                <a href={localizePublicHref(COOKIE_POLICY_PATH, language)} className="brand-link brand-focus-ring rounded-sm underline underline-offset-4">
                  {t('footer.cookiePolicy')}
                </a>
              </p>
              {isConfiguring && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <CookieOptionCard
                    title={copy.audienceMeasurement}
                    description={copy.audienceMeasurementDescription}
                    checked={audienceMeasurement}
                    onChange={setAudienceMeasurement}
                  />
                  <CookieOptionCard
                    title={copy.communicationTracking}
                    description={copy.communicationTrackingDescription}
                    checked={communicationTracking}
                    onChange={setCommunicationTracking}
                  />
                </div>
              )}
            </div>
            <div className={`flex shrink-0 gap-2 ${isConfiguring ? 'flex-col sm:flex-row' : 'flex-row'}`}>
              <button
                type="button"
                onClick={acceptAll}
                className={`brand-button brand-button-primary brand-focus-ring min-h-9 rounded-lg px-3 text-xs font-bold ${isConfiguring ? '' : 'flex-1 sm:flex-none'}`}
              >
                {copy.accept}
              </button>
              {isConfiguring ? (
                <button
                  type="button"
                  onClick={saveConfiguration}
                  className="brand-button brand-button-secondary brand-focus-ring min-h-9 rounded-lg px-3 text-xs font-bold"
                >
                  {copy.save}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsConfiguring(true)}
                  className="brand-button brand-button-secondary brand-focus-ring min-h-9 flex-1 rounded-lg px-3 text-xs font-bold sm:flex-none"
                >
                  {copy.configure}
                </button>
              )}
            </div>
          </div>
        </aside>
    ) : null
  );
}
